import React, { useState, useEffect } from "react";
import { IonItem, IonText, IonSpinner } from "@ionic/react";
import { FitnessDataType, FitnessData } from "refsix-js-models";
import { getDatabase } from "../services/database/fitnessDatabases.service";
import { runSync } from "../services/fitnessDataSync/fitnessDataSyncService";
import config from "../services/fitnessDataSync/config";
import FitnessDataItem from "./FitnessDataItem";

interface MatchFitnessData {
    matchId: string;
    gpsData?: FitnessData;
    heartRateData?: FitnessData;
    gpsSynced: boolean;
    heartRateSynced: boolean;
}

interface SyncingState {
    [matchId: string]: {
        gps: boolean;
        heartRate: boolean;
    };
}

interface FitnessDataItemWrapperProps {
    matchId: string;
    showDebugInfo?: boolean;
    onSyncComplete?: (matchId: string, dataType: FitnessDataType, success: boolean) => void;
}

export const FitnessDataItemWrapper: React.FC<FitnessDataItemWrapperProps> = ({
    matchId,
    showDebugInfo = false,
    onSyncComplete,
}) => {
    const [fitnessData, setFitnessData] = useState<MatchFitnessData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [syncing, setSyncing] = useState<SyncingState>({});

    useEffect(() => {
        loadFitnessDataForMatch();
    }, [matchId]);

    const loadFitnessDataForMatch = async () => {
        try {
            setLoading(true);
            setError(null);

            // Get both GPS and HeartRate databases
            const gpsDb = getDatabase(FitnessDataType.GPS);
            const heartRateDb = getDatabase(FitnessDataType.HeartRate);

            // Try to get data for this specific match
            const [gpsData, heartRateData] = await Promise.allSettled([
                gpsDb.get(matchId),
                heartRateDb.get(matchId)
            ]);

            const matchData: MatchFitnessData = {
                matchId,
                gpsSynced: false,
                heartRateSynced: false,
            };

            // Process GPS data
            if (gpsData.status === 'fulfilled') {
                matchData.gpsData = gpsData.value;
                matchData.gpsSynced = (gpsData.value as any).synced || false;
            }

            // Process HeartRate data
            if (heartRateData.status === 'fulfilled') {
                matchData.heartRateData = heartRateData.value;
                matchData.heartRateSynced = (heartRateData.value as any).synced || false;
            }

            // Only set fitness data if we found at least one type of data
            if (matchData.gpsData || matchData.heartRateData) {
                setFitnessData(matchData);
            } else {
                setFitnessData(null);
            }
        } catch (err) {
            console.error("Error loading fitness data for match:", err);
            setError("Failed to load fitness data");
            setFitnessData(null);
        } finally {
            setLoading(false);
        }
    };

    const syncMatchData = async (matchId: string, dataType: FitnessDataType) => {
        try {
            // Set syncing state
            setSyncing(prev => ({
                ...prev,
                [matchId]: {
                    ...prev[matchId],
                    [dataType === FitnessDataType.GPS ? "gps" : "heartRate"]: true,
                },
            }));

            const database = getDatabase(dataType);
            const dataConfig = config[dataType];

            // Mark data as unsynced to trigger sync
            await database.upsert(
                matchId,
                (doc) => ({ ...doc, synced: false } as any)
            );

            // Run sync for this specific data
            await runSync(database, dataConfig);

            // Reload data to reflect changes
            await loadFitnessDataForMatch();

            // Call callback if provided
            onSyncComplete?.(matchId, dataType, true);
        } catch (err) {
            console.error(`Error syncing ${dataType} data:`, err);
            onSyncComplete?.(matchId, dataType, false);
        } finally {
            // Clear syncing state
            setSyncing(prev => ({
                ...prev,
                [matchId]: {
                    ...prev[matchId],
                    [dataType === FitnessDataType.GPS ? "gps" : "heartRate"]: false,
                },
            }));
        }
    };

    if (loading) {
        return (
            <IonItem>
                <IonSpinner slot="start" />
                <IonText>Loading fitness data...</IonText>
            </IonItem>
        );
    }

    if (error) {
        return (
            <IonItem>
                <IonText color="danger">Error: {error}</IonText>
            </IonItem>
        );
    }

    if (!fitnessData) {
        return (
            <IonItem>
                <IonText color="medium">No fitness data found for this match</IonText>
            </IonItem>
        );
    }

    return (
        <FitnessDataItem
            fitnessData={fitnessData}
            syncing={syncing}
            onSync={syncMatchData}
            showDebugInfo={showDebugInfo}
        />
    );
};

export default FitnessDataItemWrapper;